import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

import SatisfactionTestIcon from '../../assets/icons/satisfaction-test.svg?react';
import videoIcon from '../../assets/icons/vc-camara.png';
import unsuccessIcon from '../../assets/icons/vc-sinexito.png';
import phoneIcon from '../../assets/icons/vc-telf.png';
import { NotificationPage } from '../../components/NotificationPage/NotificationPage';
import { RectangleSelector } from '../../components/RectangleSelector/RectangleSelector';
import { Item } from '../../components/RectangleSelector/SelectableRectangle';
import { useTranslationsContext } from '../../../features/Translations/providers/TranslationsContextProvider';
import { useThemeContext } from '../../../features/Theme/providers/ThemeContextProvider';
import { useMeetContext, MeetRepository, FeedbackSendParams, MeetDomainEvents } from '../../../features/Meet';
import { publish, subscribe, unsubscribe } from '../../../features/EventsBus/services/service';
import { TemporalAlert } from '../../components/Alert/TemporalAlert';

export interface FeedbackSectionProps {
	meetRepository: MeetRepository;
	code?: string;
	tenant?: string;
}

export function FeedbackPage({ meetRepository, code, tenant }: Readonly<FeedbackSectionProps>) {
	const { t } = useTranslationsContext();
	const theme = useThemeContext();
	const { meet } = useMeetContext();
	const navigate = useNavigate();

	const [selectedOption, setSelectedOption] = useState<Item | undefined>(undefined);
	const [selectedSubOption, setSelectedSubOption] = useState<Item | undefined>(undefined);
	const [showFeedbackFailAlert, setShowFeedbackFailAlert] = useState<boolean>(false);
	const [showFeedbackAlreadyRegistedAlert, setShowFeedbackAlreadyRegistedAlert] = useState<boolean>(false);

	const feedbackOptions = [
		{
			key: 'video_success_by_video',
			append: <img src={videoIcon} alt="video_success_by_video" />,
			label: t('feedback.success_by_video'),
		},
		{
			key: 'video_success_by_phone',
			append: <img src={phoneIcon} alt="video_success_by_phone" />,
			label: t('feedback.success_by_phone'),
		},
		{
			key: 'video_unsuccess',
			append: <img src={unsuccessIcon} alt="video_unsuccess" />,
			label: t('feedback.unsuccess'),
			subItems: [
				{
					key: 'video_unsuccess_patient_not_shown_up',
					label: t('feedback.patient_not_shown_up'),
				},
				{
					key: 'video_unsuccess_patient_could_not_connec',
					label: t('feedback.patient_could_not_connect'),
				},
				{
					key: 'video_unsuccess_other',
					label: t('feedback.other'),
				},
			],
		},
	];

	const sendFeedBack = useCallback(async (feedback: string) => {
		if (!code) {
			return;
		}
		publish(MeetDomainEvents.feedbackSending);
		try {
			let params: FeedbackSendParams = {
				feedback,
				code: code
			};

			if (meet && meet.code === code) {
				params = {
					...params,
					videoId: meet.videoId,
					memberId: meet.memberId,
					token: meet.connection.token,
				};
			} else {
				const feedbackData = await meetRepository.retrieveFeedbackData(code);
				params = {
					...params,
					videoId: feedbackData.videoId,
					memberId: feedbackData.memberId,
					token: feedbackData.token,
				};
			}

			await meetRepository.sendFeedback(params).then(async (response) => {
				if (response.ok) {
					publish(MeetDomainEvents.feedbackSent);
					return;
				}
				if (response.status === 409) {
					publish(MeetDomainEvents.feedbackAlreadyRegistered);
					return;
				}

				const errResponse = await response.json();
				publish(MeetDomainEvents.feedbackSendFailed, {
					error: errResponse,
				});
			});
		} catch (error) {
			if (error instanceof Error && error.message === 'FEEDBACK_ALREADY_EXISTS') {
				publish(MeetDomainEvents.feedbackAlreadyRegistered);
				return;
			}

			publish(MeetDomainEvents.feedbackSendFailed, {
				error: error,
			});
		}
	}, [meetRepository, meet, code]);

	const redirectToFinish = useCallback(() => {
		const tenantParam = tenant ? `/tenant/${tenant}` : '';
		navigate(`/finish/c/${code}${tenantParam}`, { replace: true });
	}, [code, tenant]);

	const selectOption = useCallback(async (item: Item) => {
		setSelectedOption(item);

		if (!item.subItems) {
			await sendFeedBack(item.key);
		}
	}, []);;

	const selectSubOption = useCallback(async (item: Item) => {
		setSelectedSubOption(item);

		await sendFeedBack(item.key);
	}, []);

	const showFeedbackFailAlertCallback = useCallback(() => {
		setShowFeedbackFailAlert(true);
	}, []);

	const closeFeedbackFailAlertCallback = useCallback(() => {
		setShowFeedbackFailAlert(false);
	}, []);

	const showFeedbackAlreadyRegistedCallback = useCallback(() => {
		setShowFeedbackAlreadyRegistedAlert(true);
	}, []);

	const closeFeedbackAlreadyRegistedAlertCallback = useCallback(() => {
		setShowFeedbackAlreadyRegistedAlert(false);
		redirectToFinish();
	}, []);

	useEffect(() => {
		subscribe(MeetDomainEvents.feedbackSent, redirectToFinish);
		subscribe(MeetDomainEvents.feedbackSendFailed, showFeedbackFailAlertCallback);
		subscribe(MeetDomainEvents.feedbackAlreadyRegistered, showFeedbackAlreadyRegistedCallback);

		return () => {
			unsubscribe(MeetDomainEvents.feedbackSent, redirectToFinish);
			unsubscribe(MeetDomainEvents.feedbackSendFailed, showFeedbackFailAlertCallback);
			unsubscribe(MeetDomainEvents.feedbackAlreadyRegistered, showFeedbackAlreadyRegistedCallback);
		};
	}, []);

	useEffect(() => {
		publish(MeetDomainEvents.feedbackPage);
	}, []);

	return (
		<>
			<TemporalAlert show={showFeedbackAlreadyRegistedAlert} type="warning" message={t('feedback.already_registered_message')} timeInMs={3000} onClose={closeFeedbackAlreadyRegistedAlertCallback} />
			<TemporalAlert show={showFeedbackFailAlert} type="error" message={t('feedback.fail_message')} timeInMs={3000} onClose={closeFeedbackFailAlertCallback} />
			<NotificationPage
				prepend={
					<SatisfactionTestIcon fill={theme.colors.brand.primary[300]} width={25} height={25} />
				}
				title={t('feedback.title')}
			>
				<RectangleSelector
					items={feedbackOptions}
					selected={selectedOption}
					selectedSubOption={selectedSubOption}
					onSelect={selectOption}
					onSubOptionSelect={selectSubOption}
				/>
			</NotificationPage>
		</>
	);
}
